import { useMemo } from 'react';
import { FileList, GetFileContentsEntityTypes } from 'components/FileList/FileList';
import TabularList from 'components/TabularList/TabularList';
import UploadFile from 'components/UploadFile/UploadFile';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';
export function ArmyRespiteDocumentDetails({
  application,
  careFacility,
  applicationFiles,
  careFacilityFiles,
  orgFiles,
  setApplicationFiles,
  setCareFacilityFiles,
  setOrgFiles,
  isEditable = true,
}) {
  const readOnly = !isEditable;
  const careFacilityAndOrgFiles = useMemo(
    () => [...(careFacilityFiles || []), ...(orgFiles || [])],
    [careFacilityFiles, orgFiles],
  );

  // Computed values for each file type
  const applicationSummaryFiles = useMemo(
    () => applicationFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.APPLICATION_SUMMARY.code) || [],
    [applicationFiles],
  );
  const discountVerificationFiles = useMemo(
    () => applicationFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.DISCOUNT_VERIFICATION.code) || [],
    [applicationFiles],
  );
  const w9Files = useMemo(
    () => careFacilityAndOrgFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.W9.code) || [],
    [careFacilityAndOrgFiles],
  );
  const careLocationW9Files = useMemo(
    () => careFacilityFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.W9.code) || [],
    [careFacilityFiles],
  );
  const orgEinCertificateFiles = useMemo(
    () => orgFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.EIN.code) || [],
    [orgFiles],
  );
  const careLocationEinCertificateFiles = useMemo(
    () => careFacilityFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.EIN.code) || [],
    [careFacilityFiles],
  );
  const orgVoidedCheckOrBankLetterFiles = useMemo(
    () => orgFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.DIRECT_DEPOSIT.code) || [],
    [orgFiles],
  );
  const careLocationVoidedCheckOrBankLetterFiles = useMemo(
    () => careFacilityFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.DIRECT_DEPOSIT.code) || [],
    [careFacilityFiles],
  );
  const licenseFiles = useMemo(
    () => careFacilityFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.STATE_LICENSE.code) || [],
    [careFacilityFiles],
  );
  const accreditationFiles = useMemo(
    () => applicationFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.NATIONAL_ACCREDITATION.code) || [],
    [applicationFiles],
  );
  const rateSheetFiles = useMemo(
    () => applicationFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.RATE_SHEET.code) || [],
    [applicationFiles],
  );
  const otherFiles = useMemo(
    () => applicationFiles?.filter((file) => file?.fileType === PROVIDER_FILE_TYPES.OTHER.code) || [],
    [applicationFiles],
  );
  const _fileListProps = {
    entityType: GetFileContentsEntityTypes.PROVIDER_APPLICATION,
    applicationId: application?.id,
  };
  return (
    <TabularList
      indented
      data={[
        {
          title: 'Application Summary',
          value: <FileList {..._fileListProps} files={applicationSummaryFiles} />,
        },
        {
          title: 'Discount Verification',
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.DISCOUNT_VERIFICATION.code}
              files={discountVerificationFiles}
              setFiles={setApplicationFiles}
              applicationId={application?.id}
              readOnly={readOnly}
            />
          ),
        },
        {
          title: 'Organization W-9',
          titleRequired: true,
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.W9.code}
              files={w9Files}
              setFiles={setOrgFiles}
              orgId={application?.careFacility?.orgId}
              preventDeleteIfLastFile
              readOnly={readOnly}
            />
          ),
        },
        ...(careFacility?.taxDetailsSameAsOrg
          ? []
          : [
              {
                title: 'Care location W-9',
                titleRequired: true,
                value: (
                  <UploadFile
                    fileCode={PROVIDER_FILE_TYPES.W9.code}
                    files={careLocationW9Files}
                    setFiles={setCareFacilityFiles}
                    careFacilityId={application?.careFacility?.id}
                    preventDeleteIfLastFile
                    readOnly={readOnly}
                  />
                ),
              },
            ]),
        {
          title: 'Organization EIN Certificate',
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.EIN.code}
              files={orgEinCertificateFiles}
              setFiles={setOrgFiles}
              orgId={application?.careFacility?.orgId}
              readOnly={readOnly}
            />
          ),
        },
        ...(careFacility?.taxDetailsSameAsOrg
          ? []
          : [
              {
                title: 'Care location EIN Certificate',
                value: (
                  <UploadFile
                    fileCode={PROVIDER_FILE_TYPES.EIN.code}
                    files={careLocationEinCertificateFiles}
                    setFiles={setCareFacilityFiles}
                    careFacilityId={application?.careFacility?.id}
                    readOnly={readOnly}
                  />
                ),
              },
            ]),
        {
          title: 'Organization voided check or bank letter',
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.DIRECT_DEPOSIT.code}
              files={orgVoidedCheckOrBankLetterFiles}
              setFiles={setOrgFiles}
              orgId={application?.careFacility?.orgId}
              readOnly={readOnly}
            />
          ),
        },
        ...(careFacility?.bankingDetailsSameAsOrg
          ? []
          : [
              {
                title: 'Care location voided check or bank letter',
                value: (
                  <UploadFile
                    fileCode={PROVIDER_FILE_TYPES.DIRECT_DEPOSIT.code}
                    files={careLocationVoidedCheckOrBankLetterFiles}
                    setFiles={setCareFacilityFiles}
                    careFacilityId={application?.careFacility?.id}
                    readOnly={readOnly}
                  />
                ),
              },
            ]),
        {
          title: 'Licenses',
          titleRequired: true,
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.STATE_LICENSE.code}
              files={licenseFiles}
              setFiles={setCareFacilityFiles}
              careFacilityId={application?.careFacility?.id}
              preventDeleteIfLastFile
              readOnly={readOnly}
            />
          ),
        },
        {
          title: 'Accreditations',
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.NATIONAL_ACCREDITATION.code}
              files={accreditationFiles}
              setFiles={setApplicationFiles}
              applicationId={application?.id}
              readOnly={readOnly}
            />
          ),
        },
        {
          title: 'Rate sheet',
          titleRequired: true,
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.RATE_SHEET.code}
              files={rateSheetFiles}
              setFiles={setApplicationFiles}
              applicationId={application?.id}
              preventDeleteIfLastFile
              readOnly={readOnly}
            />
          ),
        },
        {
          title: 'Other documents',
          value: (
            <UploadFile
              fileCode={PROVIDER_FILE_TYPES.OTHER.code}
              files={otherFiles}
              setFiles={setApplicationFiles}
              applicationId={application?.id}
              readOnly={readOnly}
            />
          ),
        },
      ]}
    />
  );
}
